/**
 * Single source of truth for the Otto mission vocabulary: the statuses of
 * `work_agent_threads` and the action types of `work_agent_actions`.
 *
 * Six divergent copies of "which statuses wait for a human" used to live in the
 * front (MissionPanel, ConversationRow, ActiveMissionsPanel, WorkAgentThreadPanel,
 * useActiveMissions, plus the SQL filter of `get_inbox_mission_statuses`), and two
 * of them silently omitted `proposed`/`awaiting_validation` — so the very missions
 * blocking on Gilles were the ones not flagged as blocking. One list, one answer
 * (CLAUDE.md principle 11: a rule has a single definition, every surface reads it).
 *
 * Pure module: no I/O, no Tailwind. Colours stay in the components because they
 * depend on the surface palette (Relations vs Travail), the vocabulary does not.
 */

/** Every `work_agent_threads.status` value live in DB (8, checked 15/07/2026). */
export const MISSION_STATUSES = [
  'active',
  'waiting',
  'awaiting_human',
  'proposed',
  'awaiting_validation',
  'completed',
  'failed',
  'canceled',
] as const;

export type MissionStatus = (typeof MISSION_STATUSES)[number];

/**
 * Every `work_agent_actions.action_type` value the agent can plan (9, same order
 * as `work_agent_actions_action_type_check`, checked in prod 15/07/2026). That
 * CHECK constraint is the source: what the DB accepts is what can reach the UI.
 * `agent_reassess` was added to it by 20260710230000 without this copy following,
 * so the wake-up step displayed its raw slug on Gilles's mission card.
 */
export const MISSION_ACTION_TYPES = [
  'find_contacts',
  'draft_email',
  'send_email',
  'wait_for_reply',
  'follow_up',
  'check_human_signal',
  'complete_task',
  'escalate_human',
  'agent_reassess',
] as const;

export type MissionActionType = (typeof MISSION_ACTION_TYPES)[number];

/**
 * French labels, written from Gilles's point of view ("Otto t'attend"), not from
 * the agent's — the reader of these labels is the human being waited on.
 */
export const MISSION_STATUS_LABEL: Record<MissionStatus, string> = {
  active: 'En cours',
  waiting: "En attente d'une réponse",
  awaiting_human: "Otto t'attend",
  proposed: 'Plan proposé',
  awaiting_validation: 'À valider',
  completed: 'Terminée',
  failed: 'Échec',
  canceled: 'Annulée',
};

/**
 * Exhaustive over the 9 action types: a missing entry leaks the raw slug into the
 * UI — it happened twice ("check_human_signal", then "agent_reassess"). The type
 * makes the omission a compile error only if MISSION_ACTION_TYPES itself is kept
 * in sync with the DB CHECK; the test guards that.
 */
export const ACTION_TYPE_LABEL: Record<MissionActionType, string> = {
  find_contacts: 'Retrouver les bons contacts',
  draft_email: 'Préparer un brouillon de mail',
  send_email: 'Envoyer le mail',
  wait_for_reply: 'Attendre la réponse',
  follow_up: 'Relancer',
  check_human_signal: 'Vérifier si tu as répondu',
  complete_task: 'Clôturer la mission',
  escalate_human: 'Te demander un arbitrage',
  agent_reassess: 'Reprendre la mission et décider de la suite',
};

/**
 * Statuses where Otto has stopped and cannot move without Gilles. `waiting` is
 * NOT one of them: there Otto waits on a third party (a client reply) and will
 * wake up on its own — nothing is asked of Gilles.
 */
const WAITING_FOR_HUMAN: ReadonlySet<MissionStatus> = new Set<MissionStatus>([
  'awaiting_human',
  'proposed',
  'awaiting_validation',
]);

/** Statuses of a mission still alive (not terminal) — the ones worth showing. */
export const MISSION_LIVE_STATUSES: readonly MissionStatus[] = [
  'active',
  'waiting',
  'awaiting_human',
  'proposed',
  'awaiting_validation',
];

/**
 * Live statuses where Otto moves on its own (running, or waiting on a third party):
 * what « Otto travaille » lists. Derived, so it cannot drift from the two sets above.
 */
export const MISSION_RUNNING_STATUSES: readonly MissionStatus[] = MISSION_LIVE_STATUSES.filter(
  (status) => !WAITING_FOR_HUMAN.has(status),
);

/** Narrows an unknown string (RPC payloads are untyped) to the known vocabulary. */
export function isMissionStatus(value: string | null | undefined): value is MissionStatus {
  return value != null && (MISSION_STATUSES as readonly string[]).includes(value);
}

export function isMissionActionType(value: string | null | undefined): value is MissionActionType {
  return value != null && (MISSION_ACTION_TYPES as readonly string[]).includes(value);
}

/**
 * Does this mission need Gilles to move? Accepts a raw string so callers can pass
 * an unchecked RPC value; an unknown status answers `false` rather than throwing —
 * inventing an alarm on vocabulary we do not know is worse than staying silent.
 */
export function missionWaitsForHuman(status: string | null | undefined): boolean {
  return isMissionStatus(status) && WAITING_FOR_HUMAN.has(status);
}

/** Is the mission still running (as opposed to completed/failed/canceled)? */
export function isMissionLive(status: string | null | undefined): boolean {
  return isMissionStatus(status) && (MISSION_LIVE_STATUSES as readonly string[]).includes(status);
}

/** Label of a status, falling back to the raw value so an unknown one stays visible. */
export function missionStatusLabel(status: string): string {
  return isMissionStatus(status) ? MISSION_STATUS_LABEL[status] : status;
}

/** Label of an action type, falling back to the raw value (same rationale). */
export function actionTypeLabel(actionType: string): string {
  return isMissionActionType(actionType) ? ACTION_TYPE_LABEL[actionType] : actionType;
}
